import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CalendarClock,
  Camera,
  LogOut,
  Phone,
  Plus,
  Receipt,
  RefreshCw,
  Star,
  Truck,
} from 'lucide-react';
import { JobStatusBadge, PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Field,
  Input,
  Loading,
  Modal,
  Select,
  StarRating,
  Stat,
  Textarea,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../context/AuthContext.jsx';
import { FREIGHT_PLAIN, countOf, dollars, whenPlain } from '../lib/plain.js';
import { dateLabel } from '../lib/format.js';
import BrandMark from '../components/BrandMark.jsx';

export default function ClientPortal() {
  const { actor, logout } = useAuth();
  const toast = useToast();
  const { data, loading, refresh } = useApi('/client/summary', { deps: [actor?.customerId] });
  const [rateJob, setRateJob] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);

  if (loading && !data) {
    return (
      <>
        <PageHead title="Your dashboard" subtitle="Getting your jobs…" />
        <Loading rows={4} />
      </>
    );
  }
  if (!data) {
    return (
      <EmptyState icon={Building2} title="We could not load your account">
        <Button variant="primary" onClick={refresh}>Try again</Button>
      </EmptyState>
    );
  }

  const { customer, sites, jobs, invoices, kpis } = data;
  const unrated = jobs.filter((job) => job.lumperName && !job.rating && ['completed', 'verified', 'paid', 'invoiced'].includes(job.status));

  return (
    <>
      <div className="util-bar">
        <div className="util-bar-inner">
          <Link to="/" className="row gap-10">
            <BrandMark size={26} />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 650 }}>3Lumpers</span>
          </Link>
          <span className="dim">·</span>
          <span className="dim">{customer.name}</span>
          <div className="grow" />
          <a href="tel:+18552327996" className="row gap-6"><Phone size={13} /> 1-855-232-7996</a>
          <Button
            size="sm"
            variant="outline"
            icon={LogOut}
            onClick={async () => {
              await logout();
              window.location.href = '/login';
            }}
          >
            Sign out
          </Button>
        </div>
      </div>

      <div className="signup-shell">
        <PageHead
          title={`Hello ${String(actor?.name || '').split(' ')[0]}`}
          subtitle={`Everything for ${customer.name} in one place. Ask for a crew whenever a trailer turns up.`}
          icon={Building2}
          actions={
            <>
              <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
              <Button variant="primary" size="sm" icon={Plus} onClick={() => setRequestOpen(true)}>
                Ask for a crew
              </Button>
            </>
          }
        />

        {unrated.length ? (
          <Callout
            tone="amber"
            icon={Star}
            title={`${countOf(unrated.length, 'finished job')} still needs a rating`}
            className="mb-20"
          >
            Telling us how the crew did is how we send you better ones next time.
          </Callout>
        ) : null}

        <div className="grid grid-4 mb-20">
          <Stat label="Jobs coming up" icon={CalendarClock} value={kpis?.open || 0} tone="amber" foot={`${kpis?.waiting || 0} waiting on us`} />
          <Stat label="Jobs finished" icon={Truck} value={kpis?.done || 0} tone="green" foot={`${countOf(kpis?.pallets || 0, 'pallet')} moved`} />
          <Stat label="Money owed" icon={Receipt} value={dollars(kpis?.owed)} tone={Number(kpis?.owed) > 0 ? 'red' : 'green'} foot="across your invoices" />
          <Stat
            label="Crews you have rated"
            icon={Star}
            value={kpis?.avgRating ? Number(kpis.avgRating).toFixed(1) : '—'}
            tone="violet"
            foot={kpis?.avgRating ? 'your average score' : 'no ratings yet'}
          />
        </div>

        <div className="split mb-20">
          <Card>
            <CardHead title="Your jobs" subtitle="Newest first" icon={Truck} />
            {jobs.length ? (
              <div className="table-wrap">
                <table className="table plain">
                  <thead>
                    <tr>
                      <th className="narrow">When</th>
                      <th>Where</th>
                      <th>What</th>
                      <th className="narrow">Who came</th>
                      <th className="narrow">Status</th>
                      <th className="narrow right" />
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id}>
                        <td className="narrow">
                          <div className="col">
                            <span className="num" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
                              {dateLabel(job.windowStart)}
                            </span>
                            <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                              {whenPlain(job.windowStart).toLowerCase()}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="col gap-2">
                            <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.siteName || 'Your dock'}</span>
                            <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{job.siteCity}, {job.siteState}</span>
                          </div>
                        </td>
                        <td>
                          <div className="col gap-2">
                            <span style={{ fontSize: 'var(--fs-sm)' }}>{FREIGHT_PLAIN[job.freightType] || job.freightType}</span>
                            <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                              {job.palletsActual || job.palletsExpected} pallets
                              {job.photoCount ? ` · ${job.photoCount} photos` : ''}
                            </span>
                          </div>
                        </td>
                        <td className="narrow">
                          {job.lumperName ? (
                            <span className="row gap-8">
                              {job.lumperPhoto ? (
                                <img src={job.lumperPhoto} alt="" className="avatar sm" style={{ objectFit: 'cover' }} />
                              ) : (
                                <Avatar name={job.lumperName} tone="blue" size="sm" />
                              )}
                              <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.lumperName}</span>
                            </span>
                          ) : (
                            <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Being arranged</span>
                          )}
                        </td>
                        <td className="narrow"><JobStatusBadge status={job.status} size="sm" short dot={false} /></td>
                        <td className="narrow right">
                          {job.lumperName && !job.rating && ['completed', 'verified', 'paid', 'invoiced'].includes(job.status) ? (
                            <Button size="sm" variant="primary" icon={Star} onClick={() => setRateJob(job)}>
                              Rate the crew
                            </Button>
                          ) : job.rating ? (
                            <span className="row gap-6" style={{ justifyContent: 'flex-end' }}>
                              <StarRating value={job.rating} readOnly size={13} />
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon={Truck} title="No jobs yet">
                Ask for a crew and it will show up here with photos and the pallet count.
              </EmptyState>
            )}
          </Card>

          <div className="col gap-20">
            <Card>
              <CardHead title="Your bills" subtitle="One invoice a month" icon={Receipt} />
              <CardBody className="col gap-10">
                {invoices.length ? (
                  invoices.map((invoice) => (
                    <div key={invoice.id} className="row between gap-10">
                      <span className="col gap-2">
                        <span className="row gap-6">
                          <span className="mono" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{invoice.code}</span>
                          <Badge
                            size="sm"
                            tone={invoice.status === 'paid' ? 'green' : invoice.balance > 0 ? 'amber' : 'muted'}
                          >
                            {invoice.status === 'paid' ? 'Paid' : invoice.balance > 0 ? 'Owing' : 'Clear'}
                          </Badge>
                        </span>
                        <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                          {invoice.jobsCount} jobs · due {dateLabel(invoice.dueAt)}
                        </span>
                      </span>
                      <span className="col gap-2" style={{ alignItems: 'flex-end' }}>
                        <span className="num" style={{ fontWeight: 620 }}>{dollars(invoice.total)}</span>
                        {Number(invoice.balance) > 0 ? (
                          <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{dollars(invoice.balance)} to pay</span>
                        ) : null}
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                    Nothing billed yet. We send one invoice covering the month.
                  </span>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Your docks" icon={Building2} />
              <CardBody className="col gap-10">
                {sites.map((site) => (
                  <div key={site.id} className="col gap-2">
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{site.name}</span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {site.address ? `${site.address}, ` : ''}{site.city}, {site.state} {site.zip}
                    </span>
                    <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                      Open {site.dockHours} · {site.dockDoors} door{site.dockDoors === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
                {!sites.length ? <span className="dim">No docks on file yet — call us and we will add one.</span> : null}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <RequestModal
        open={requestOpen}
        sites={sites}
        options={data.options}
        onClose={() => setRequestOpen(false)}
        onSent={() => {
          setRequestOpen(false);
          refresh();
        }}
      />

      <RateModal job={rateJob} onClose={() => setRateJob(null)} onSaved={() => { setRateJob(null); refresh(); }} toast={toast} />
    </>
  );
}

function RequestModal({ open, sites, options, onClose, onSent }) {
  const toast = useToast();
  const [form, setForm] = useState({ siteId: '', pallets: 24, when: '', freightType: 'floor_loaded', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await api.post('/client/requests', {
        siteId: form.siteId ? Number(form.siteId) : sites[0]?.id,
        pallets: Number(form.pallets),
        when: new Date(form.when).toISOString(),
        freightType: form.freightType,
        notes: form.notes || undefined,
      });
      toast.ok('Request sent', payload.message || 'The office will confirm the crew and price.');
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ask for a crew"
      subtitle="Tell us what is turning up and when"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={submit}>Send request</Button>
        </>
      }
    >
      <div className="col gap-16">
        {error ? <Callout tone="red">{error}</Callout> : null}
        <Field label="Which dock?" hint={sites.length ? undefined : 'We will use the address on your account'}>
          <Select
            value={form.siteId}
            onChange={(e) => set('siteId', e.target.value)}
            placeholder={sites.length ? 'Choose a dock' : 'No docks on file yet'}
            options={sites.map((site) => ({ value: site.id, label: site.name }))}
          />
        </Field>
        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="How many pallets?">
            <Input className="mono" type="number" value={form.pallets} onChange={(e) => set('pallets', e.target.value)} />
          </Field>
          <Field label="When does it arrive?" required>
            <Input type="datetime-local" value={form.when} onChange={(e) => set('when', e.target.value)} />
          </Field>
        </div>
        <Field label="What kind of load?">
          <Select
            value={form.freightType}
            onChange={(e) => set('freightType', e.target.value)}
            options={(options?.freightTypes || []).map((type) => ({ value: type.key, label: type.label }))}
          />
        </Field>
        <Field label="Anything the crew should know?">
          <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Two trailers arriving together, back in only." />
        </Field>
        <Callout tone="blue" icon={Camera}>
          The crew photographs the load and counts the pallets, so you both see the same numbers.
        </Callout>
      </div>
    </Modal>
  );
}

function RateModal({ job, onClose, onSaved, toast }) {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="How did the crew do?"
      subtitle={job ? `${job.lumperName} · ${dateLabel(job.windowStart)} at ${job.siteName || 'your dock'}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Not now</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/client/jobs/${job.id}/rate`, { stars, comment: comment || undefined });
                toast.ok('Thanks', 'We use this to send you the right crew next time.');
                onSaved();
              } catch (err) {
                toast.err('Could not save that', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save rating
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="col gap-8" style={{ alignItems: 'center' }}>
          <StarRating value={stars} onChange={setStars} size={34} />
          <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
            {['Poor', 'Not great', 'Fine', 'Good', 'Excellent'][stars - 1]}
          </span>
        </div>
        <Field label="Anything to add? (optional)">
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Quick, careful and cleaned up after themselves."
          />
        </Field>
        <Callout tone="amber" icon={Star}>
          Ratings are only ever shown as an average on the lumper's record — the office sees your words, other
          customers do not.
        </Callout>
      </div>
    </Modal>
  );
}
